import type { DeliveryMethods, ETADeliveryProvidersResponse } from '~/api/ETA';

import { ENDPOINT } from './constants';
import { RequestAPI } from './Requests';

export type AddressUsage = 'pickup' | 'delivery';

export interface AutocompleteSuggestion {
  address: string;
  postcode: string;
  location: {
    latitude: number;
    longitude: number;
  };
  country?: string;
  city?: string;
  place_id: string | null;
}

export interface Address {
  city: string;
  comment: string | null;
  customer_id: string;
  id: string;
  instructions: string[] | null;
  latitude: number;
  longitude: number;
  place_id: string;
  postcode: string;
  street_address_1: string | null;
  street_address_2: string | null;
  building: string | null;
  floor: string | null;
  apartment: string | null;
  country_code: string;
  type: string | null;
  usage?: AddressUsage;
  warehouse_code?: string | null;
}

export interface AddressResponse {
  data: Address;
}

export interface AddressesResponse {
  data: Address[];
}

interface DeliveryZoneWarehouse {
  code: string;
  id: string;
  active?: boolean;
  /** Physical warehouse address, used as the pickup point label. */
  address?: string;
  /** Warehouse location, used as the pickup point marker/center. */
  latitude?: number;
  longitude?: number;
}

export interface DeliveryZoneSegment {
  start: {
    latitude: number;
    longitude: number;
  };
  end: {
    latitude: number;
    longitude: number;
  };
}

export interface DeliveryMechanicSchedule {
  dayOfWeek: number;
  intervals: Array<{ start: string; end: string }>;
}

export interface DeliveryZoneMethod {
  shippingType: DeliveryMethods;
}

export type DeliveryZoneDisplayType = 'zone' | 'pudo';

export interface DeliveryZone {
  id: string;
  warehouseId?: string;
  deliveryMethod: DeliveryZoneMethod;
  name: string;
  color: string;
  priority: number;
  polygon: {
    segments: DeliveryZoneSegment[];
  };
  description: string | null;
  displayType: DeliveryZoneDisplayType;
  /** Assortment size for this zone, shown as the "N items" pickup badge. */
  capacity?: number | null;
}

export interface DeliveryV2Zone {
  id: string;
  color: string;
  deliveryProviders: ETADeliveryProvidersResponse;
  name: string;
  paymentMethods: string[];
  polygon: {
    segments: DeliveryZoneSegment[];
  };
  priority: number;
  description: string | null;
  displayType: DeliveryZoneDisplayType;
  /** Assortment size for this zone, shown as the "N items" pickup badge. */
  capacity?: number | null;
}

export interface DeliveryZoneCoverage {
  warehouse: DeliveryZoneWarehouse;
  zones: (DeliveryZone | undefined)[];
}

export interface DeliveryV2ZoneCoverage extends DeliveryZoneCoverage {
  v2zones: DeliveryV2Zone[];
}

interface DeliveryZonesResponse<T> {
  data: {
    coverages: T[];
  };
}

export interface AddAddressRequest {
  street_address_1: string;
  city: string;
  postcode: string;
  latitude: number;
  longitude: number;
  country: string;
  street_address_2: string;
  apartment?: string;
  floor?: string;
  building: string;
  comment?: string;
  instructions?: string[];
  type: string;
  usage?: AddressUsage;
  warehouse_code?: string;
}

export interface UpdateAddressRequest {
  city?: string;
  comment?: string;
  instructions?: string[];
  latitude?: number;
  longitude?: number;
  postcode?: string;
  street_address_1?: string;
  street_address_2?: string;
  apartment?: string;
  floor?: string;
  building?: string;
  type?: string;
  usage?: AddressUsage;
  warehouse_code?: string;
}

export interface AutocompleteRequest {
  sessionToken: string;
  input: string;
  placeId: string;
  countryCode: string;
  languageCode: string;
}

export interface AutocompletePrediction {
  description: string;
  place_id: string;
}

export interface Location {
  latitude: number;
  longitude: number;
}

export interface AutocompletePlace {
  address: string;
  postcode: string | null;
  location: Location;
}

export interface AutocompleteResponse {
  data: {
    result: AutocompletePlace | null;
    predictions: AutocompletePrediction[];
  };
}

interface DeliveryRequestsInterface {
  autocomplete(data: AutocompleteRequest): Promise<AutocompleteResponse>;

  fetchDeliveryZones(): Promise<DeliveryZonesResponse<DeliveryV2ZoneCoverage>>;

  getAddresses(): Promise<AddressesResponse>;

  addAddress(data: AddAddressRequest): Promise<{ data: Address }>;

  updateAddress(
    id: string,
    data: UpdateAddressRequest,
  ): Promise<AddressResponse>;

  deleteAddress(id: string): Promise<AddressesResponse>;
}

export const DeliveryRequests: DeliveryRequestsInterface = {
  autocomplete: (data) =>
    RequestAPI.post(ENDPOINT.deliveryAddress.autocomplete, data),

  fetchDeliveryZones: () =>
    RequestAPI.get(ENDPOINT.deliveryAddress.zones.request),

  getAddresses: () => RequestAPI.get(ENDPOINT.customer.address.request),

  addAddress: (data) => RequestAPI.post(ENDPOINT.customer.address.add, data),

  updateAddress: (id, data) =>
    RequestAPI.patch(ENDPOINT.customer.address.update + id, data),

  deleteAddress: (id) =>
    RequestAPI.delete(ENDPOINT.customer.address.delete + id),
};
